// Reader-gated features (WF-40 round 3; ops plan 2026-10-14-combine-orders › Rollout gate). A feature that records a
// fact older register builds store without folding (Combine Orders' `OrdersAdopted`) is offered only while every active
// register at the location reads that fact. A receiver never refuses a well-formed fact, and a register decides such a
// command alone with the node down, so the gate sits on the deciding register: the button and its runtime.
//
// - Every server that answers a register's location pull (the node, or the cloud on a register's cloud route) records
//   which gated event types the register declares it reads (`maxSchemaVersions`) and when it last pulled.
// - From those rows it works out the features every ACTIVE register reads and writes them, when they change, as the
//   config entity `fleet.location_capabilities` (id = the location id), the node's view in `atNode`, the cloud's in
//   `atCloud`. Every replica syncs it, so a register offline keeps the last value it held.
// - A feature is available only when every view written lists it, and no view written means unavailable (fail closed).
//
// Generic: a new gated feature is one entry in READER_GATED_FEATURES (what it reads, which commands it gates).
import { z } from 'zod';
import { defineError } from '../define/errors.ts';
import { readableVersions, type Versioned } from '../define/upcast.ts';

export interface ReaderGatedFeature {
  /** The event types (and the oldest version of each) a register must read before the feature is offered anywhere. */
  readonly reads: Readonly<Record<string, number>>;
  /** The commands the feature records: refused, at a register and at the server, while it is unavailable. */
  readonly commands: readonly string[];
  /**
   * Commands gated only for some inputs (a new mode of an existing command): refused while the feature is unavailable
   * when the predicate holds for the input. The command's other inputs are never gated.
   */
  readonly inputs?: Readonly<Record<string, (input: unknown) => boolean>>;
}

/** Whether an `orders.close` input closes leaving a small balance unpaid (WF-47). */
const closesSmallBalance = (input: unknown): boolean =>
  typeof input === 'object' && input !== null && 'settlement' in input && typeof input.settlement === 'object' && input.settlement !== null
  && 'mode' in input.settlement && input.settlement.mode === 'small_balance';

export const READER_GATED_FEATURES = {
  // A register built before OrdersAdopted stores the fact unfolded, so its copy of the destination lacks the adopted
  // lines (platform #725). Every version reaches a v1 reader (v2 downcasts), so declaring any version is enough.
  'orders.combine': { reads: { OrdersAdopted: 1 }, commands: ['orders.combineOrders'] },
  // WF-47: Close Order with under $1.00 still due is a new mode of `orders.close` that an older node or cloud refuses as
  // invalid input. Registers read nothing new (it emits OrderClosed), so only the servers' views hold it back: a server
  // writes its view with every feature its build knows, so a view written by an older build leaves it out.
  'orders.closeSmallBalance': { reads: {}, commands: [], inputs: { 'orders.close': closesSmallBalance } },
  // Lane M round 29 (#1086) with lane C: a carried-over tab's units legacy had not sent (OrderCarriedOver v2). A v1
  // register takes every carried unit as sent, so the cutover's checked_in step waits for every unit to read v2 at a
  // location whose carried tabs have unsent units. It gates no command.
  'orders.carryOverSent': { reads: { OrderCarriedOver: 2 }, commands: [] },
} as const satisfies Record<string, ReaderGatedFeature>;

export type ReaderGatedFeatureKey = keyof typeof READER_GATED_FEATURES;

export const READER_GATED_FEATURE_KEYS = Object.keys(READER_GATED_FEATURES).sort() as readonly ReaderGatedFeatureKey[];

/**
 * How long a register counts as active after its last pull: 30 days. Nothing bounds how long a register can be offline
 * and still sync today (no stop-sale with a node, no feed retention, no key expiry), so this is a policy, not a limit:
 * a register unseen for longer no longer holds a feature back. Its first pull back counts again at once, so an older
 * build that reappears turns the feature off before anyone can combine through it again (the offline-safety note in the
 * plan covers the gap: the older register stores the fact and folds it once it updates).
 */
export const ACTIVE_REGISTER_WINDOW_DAYS = 30;
export const ACTIVE_REGISTER_WINDOW_MS = ACTIVE_REGISTER_WINDOW_DAYS * 24 * 60 * 60 * 1000;

/** What a server holds about one register: the gated event versions it declared, and when it last pulled. */
export interface RegisterReader {
  readonly reads: Readonly<Record<string, number>>;
  readonly lastPullAtMs: number;
}

/** The part of a pull's `maxSchemaVersions` the gate needs: the declared version of each gated event type, if any. */
export function gatedReadsOf(maxSchemaVersions: Readonly<Record<string, number>>): Record<string, number> {
  const types = new Set(Object.values(READER_GATED_FEATURES).flatMap((f) => Object.keys(f.reads)));
  return Object.fromEntries([...types].sort().flatMap((t) => (typeof maxSchemaVersions[t] === 'number' ? [[t, maxSchemaVersions[t]]] : [])));
}

const readsAll = (reads: Readonly<Record<string, number>>, needs: Readonly<Record<string, number>>): boolean =>
  Object.entries(needs).every(([type, version]) => (reads[type] ?? 0) >= version);

/**
 * The features every active register reads (sorted). A register is active when it pulled within the window; none
 * active leaves every feature available (nothing at the location could miss the fact).
 */
export function featuresAvailable(registers: readonly RegisterReader[], nowMs: number): ReaderGatedFeatureKey[] {
  const active = registers.filter((r) => nowMs - r.lastPullAtMs <= ACTIVE_REGISTER_WINDOW_MS);
  return READER_GATED_FEATURE_KEYS.filter((key) => active.every((r) => readsAll(r.reads, READER_GATED_FEATURES[key].reads)));
}

/** A gated command refused while its feature is unavailable at the location (at a register, or at the server). */
export const CapabilityUnavailable = defineError({
  code: 'sync.capability_unavailable', httpStatus: 409, message: 'errors.sync.capability_unavailable', retryable: false,
  details: z.object({ feature: z.string() }),
});

/** The gated feature a command records with this input, if any (`input` omitted: only whole-command gates). */
export function gatedFeatureOf(commandName: string, input?: unknown): ReaderGatedFeatureKey | undefined {
  return READER_GATED_FEATURE_KEYS.find((key) => {
    const feature: ReaderGatedFeature = READER_GATED_FEATURES[key];
    if (feature.commands.includes(commandName)) return true;
    const gates = feature.inputs?.[commandName];
    return gates !== undefined && input !== undefined && gates(input);
  });
}

/** The location's capabilities as a replica holds them: each server's view, absent until that server wrote one. */
export interface HeldCapabilities {
  readonly atNode?: readonly string[] | undefined;
  readonly atCloud?: readonly string[] | undefined;
}

/** Whether `feature` is available: every view written lists it, and at least one was written (fail closed). */
export function capabilityAvailable(held: HeldCapabilities | null, feature: ReaderGatedFeatureKey): boolean {
  const views = [held?.atNode, held?.atCloud].filter((v): v is readonly string[] => v !== undefined);
  return views.length > 0 && views.every((v) => v.includes(feature));
}

/** Whether a build that reads `events` reads every gated feature's facts (a test aid: this build must). */
export const buildReadsGatedFeatures = (events: Readonly<Record<string, Versioned>>): boolean =>
  featuresAvailable([{ reads: gatedReadsOf(readableVersions(events)), lastPullAtMs: 0 }], 0).length === READER_GATED_FEATURE_KEYS.length;
